import { useMemo } from 'react';
import { classNames as cn } from 'primereact/utils';
import logo from '@/assets/images/logo.svg';
import { selectUserAboutData, useAppSelector } from '@/store';
import { useChatQuery } from '@/ui/features/chat/useChatContext';

/** Greeting templates for the new-chat page. `{name}` is replaced with the user's name ("there" if not loaded). */
const GREETINGS: readonly string[] = [
  'Hi {name}, how can I help?',
  'Hey {name}, what’s on your mind?',
  'Welcome back {name} — what shall we work on?',
  'Good to see you {name}. Where should we start?',
  'Hi {name}, ready when you are.',
  'Hello {name}, what would you like to explore?',
  'Hey {name}, what can I help you tackle today?',
  'Hi {name}, got a case on your mind?',
  'Welcome, {name} — what’s the plan?',
  'Hey {name}, let’s get to work.',
  'Hi {name}, what would you like to look into?',
  '{name}, what can I help you with today?',
  'Ready when you are, {name}.',
  'Hi {name}, what shall we dig into?',
  'Hey {name}, need a hand with anything?',
  'Hi {name}, what’s next?',
  'Hello {name}, how can I be useful today?',
  'Hi {name}, what would you like a hand with?',
  'Welcome back {name} — where were we?',
  'Hey {name}, what would you like me to look at?',
  'Hi {name}, what would you like to work through?',
  'Hi {name}, throw something at me.',
  'Hey {name}, what’s the question?',
  'Hi {name}, what can I do for you?',
  '{name}, ready to get started?',
  'Hi {name}, what shall we crack on with?',
  'Hey {name}, tell me what you need.',
  'Hi {name}, what would you like to review?',
  'Welcome {name} — what would help most right now?',
  'Hi {name}, where would you like to start?',
  'Hey {name}, what would you like to figure out?',
  'Hi {name}, what can I help sort?',
  'Hi {name}, got a query for me?',
  'Hey {name}, what shall we untangle today?',
  'Hi {name}, need me to look something up?',
  'Welcome back, {name}. What’s the ask?',
  'Hi {name}, what would you like a second opinion on?',
  'Hey {name}, what shall we clarify?',
  'Hi {name}, what would you like to draft?',
  'Hi {name}, need me to summarise something?',
  'Hey {name}, want a hand thinking something through?',
  'Hi {name}, what’s on today’s docket?',
  'Hi {name}, what can I unpack for you?',
  'Hey {name}, what would you like a hand with?',
  'Hi {name}, ready to make progress?',
  'Hey {name}, where would you like to focus?',
  'Hi {name}, want to talk something through?',
  'Welcome, {name}. What can we get done today?',
  'Hi {name}, what’s the brief?',
  'Hi {name}, what shall we get stuck into?',
];

const pickGreetingTemplate = () => GREETINGS[Math.floor(Math.random() * GREETINGS.length)] ?? GREETINGS[0]!;

export const ChatWelcome = () => {
  const userAboutData = useAppSelector(selectUserAboutData);

  // Picked once per mount, so the greeting doesn't change while on the page.
  const template = useMemo(() => pickGreetingTemplate(), []);
  const greeting = template.replace('{name}', userAboutData?.greeting ?? 'there');

  return (
    <div className="flex flex-col items-center gap-4">
      <img src={logo} alt="Fletch Logo" className="h-[60px] w-15" />
      <h1 className="text-theme-black text-center text-2xl leading-8 font-bold">{greeting}</h1>
    </div>
  );
};

interface ChatWelcomeFaderProps {
  /** External trigger — e.g. improve-prompt clarifier panel is open. */
  hidden: boolean;
}

/**
 * Fades {@link ChatWelcome} out when `hidden` is set or the user has typed 5+ characters. Separate
 * component so `ChatView` doesn't re-render on every keystroke.
 */
export const ChatWelcomeFader = ({ hidden }: ChatWelcomeFaderProps) => {
  const { chatQuery } = useChatQuery();
  const isDismissed = hidden || chatQuery.trim().length > 5;
  return (
    <div
      aria-hidden={isDismissed}
      className={cn(
        'flex flex-col items-center overflow-hidden [@container_welcome-hub_(max-height:32.5rem)]:hidden',
        'transition-[max-height,margin-bottom,opacity] duration-300 ease-out',
        isDismissed ? 'pointer-events-none -mb-4 max-h-0 opacity-0' : 'max-h-40 opacity-100',
      )}
    >
      <ChatWelcome />
    </div>
  );
};
